import React, { useState, useRef, useEffect } from "react";
import { BASE_URL } from "utils/constant";
import { X, CheckCircle2, AlertCircle, RotateCcw, ChevronDown } from "lucide-react";

/**
 * Simplified Delivery Flow Modal
 * Removed all payment-related logic and steps.
 * Focuses only on Material Delivery Check (Matched/Faulty).
 */
const DeliveryFlow = ({
  onClose,
  delivery,
  onMarkedReceived,
  onMarkedReturned,
}) => {
  const [deliveryData, setDeliveryData] = useState(delivery);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const updateTimersRef = useRef({});
  const [openFaultForMaterialId, setOpenFaultForMaterialId] = useState(null);
  const [faultDropdownOpenFor, setFaultDropdownOpenFor] = useState(null);
  const faultDropdownWrapperRef = useRef(null);

  const normalizeCheckValue = (value) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    if (v === "matched") return "matched";
    if (v === "fault") return "fault";
    if (v === "match" || v === "ok") return "matched";
    return v;
  };

  const getMaterialOverallStatus = (material) => {
    const spec = normalizeCheckValue(material?.specification_check);
    const qty = normalizeCheckValue(material?.quantity_check);
    const qual = normalizeCheckValue(material?.quality_check);

    if (spec === "fault" || qty === "fault" || qual === "fault") return "fault";
    if (spec === "matched" && qty === "matched" && qual === "matched")
      return "matched";
    return "unset";
  };

  const handleMarkAsReceived = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/inventory/mark-as-delivered/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          delivery_id: deliveryData.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to mark as received");
      }

      if (onMarkedReceived) {
        onMarkedReceived();
      } else {
        onClose();
      }
    } catch (error) {
      setError(error.message || "Failed to process request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturn = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");

      const response = await fetch(
        `${BASE_URL}/inventory/update-delivery/${deliveryData.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            transaction_id: 3,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to process return");
      }

      if (onMarkedReturned) {
        onMarkedReturned();
      } else {
        onClose();
      }
    } catch (error) {
      setError(error.message || "Failed to process return");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!faultDropdownOpenFor) return;

    const handleClickOutside = (event) => {
      if (
        faultDropdownWrapperRef.current &&
        !faultDropdownWrapperRef.current.contains(event.target)
      ) {
        setFaultDropdownOpenFor(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [faultDropdownOpenFor]);

  const updateMaterialCheck = (materialId, checkData) => {
    setDeliveryData((prev) => ({
      ...prev,
      all_materials: prev.all_materials.map((material) =>
        material.id === materialId ? { ...material, ...checkData } : material
      ),
    }));

    if (updateTimersRef.current[materialId]) {
      clearTimeout(updateTimersRef.current[materialId]);
    }

    updateTimersRef.current[materialId] = setTimeout(async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const payload = { ...checkData };
        if (Object.prototype.hasOwnProperty.call(payload, "false_quantity")) {
          const val = payload.false_quantity;
          payload.false_quantity =
            val === "" || val === null ? null : Number(val);
        }

        await fetch(
          `${BASE_URL}/inventory/update-materials-of-delivery/${materialId}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload),
          }
        );
      } catch (error) {
        console.error("Error updating material check:", error);
      } finally {
        delete updateTimersRef.current[materialId];
      }
    }, 300);
  };

  const setMaterialMatched = (materialId) => {
    setOpenFaultForMaterialId(null);
    setFaultDropdownOpenFor(null);
    updateMaterialCheck(materialId, {
      specification_check: "matched",
      quantity_check: "matched",
      quality_check: "matched",
      specification_check_comment: "",
      false_quantity: null,
    });
  };

  const toggleFaultCategory = (material, categoryKey) => {
    const current = normalizeCheckValue(material?.[categoryKey]);
    const nextValue = current === "fault" ? "matched" : "fault";

    const patch = {
      [categoryKey]: nextValue,
    };

    if (categoryKey === "quantity_check" && nextValue !== "fault") {
      patch.false_quantity = null;
    }

    updateMaterialCheck(material.id, patch);
  };

  const updateFaultDetails = (materialId, value) => {
    updateMaterialCheck(materialId, {
      specification_check_comment: value,
    });
  };

  const updateFaultyQuantity = (materialId, value) => {
    updateMaterialCheck(materialId, {
      false_quantity: value,
      quantity_check: "fault",
    });
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl flex flex-col h-[85vh] max-h-[850px] overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-white shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Delivery Verification</h1>
            <p className="text-xs font-medium text-slate-500 mt-0.5">Check materials before receiving</p>
          </div>
          <button
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-xl text-xs font-medium border border-red-100 flex items-center gap-2">
            <AlertCircle size={14} />
            {error}
          </div>
        )}
      </div>

      {/* Materials List */}
      <div className="flex-1 overflow-y-auto p-5 bg-slate-50/50 custom-scrollbar">
        <div className="space-y-4">
          {deliveryData.all_materials.map((material, index) => {
            const status = getMaterialOverallStatus(material);
            const isFaultOpen = openFaultForMaterialId === material.id;

            return (
              <div key={material.id} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-4 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">{material.material_name}</h3>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{material.material_subcategory}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-0.5">Quantity</span>
                    <span className="text-sm font-bold text-slate-900">
                      {material.quantity} {material.unit}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 border-2 ${
                        status === "matched"
                          ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm"
                          : "bg-white border-slate-100 text-slate-400 hover:border-emerald-200 hover:text-emerald-600"
                      }`}
                      onClick={() => setMaterialMatched(material.id)}
                    >
                      <CheckCircle2 size={16} />
                      MATCHED
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 border-2 ${
                        status === "fault"
                          ? "bg-red-50 border-red-500 text-red-700 shadow-sm"
                          : "bg-white border-slate-100 text-slate-400 hover:border-red-200 hover:text-red-600"
                      }`}
                      onClick={() => setOpenFaultForMaterialId(isFaultOpen ? null : material.id)}
                    >
                      <AlertCircle size={16} />
                      FAULTY
                    </button>
                  </div>

                  {isFaultOpen && (
                    <div className="p-4 rounded-xl border border-red-100 bg-red-50/30 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="relative" ref={faultDropdownOpenFor === material.id ? faultDropdownWrapperRef : null}>
                        <label className="block text-[10px] font-bold text-red-600 uppercase tracking-widest mb-2">Select Issues</label>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-red-200 bg-white text-xs font-semibold text-slate-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFaultDropdownOpenFor(prev => prev === material.id ? null : material.id);
                          }}
                        >
                          Select categories
                          <ChevronDown size={14} className="text-red-400" />
                        </button>

                        {faultDropdownOpenFor === material.id && (
                          <div className="absolute top-full left-0 mt-2 w-full rounded-xl border border-red-100 bg-white shadow-xl z-20 overflow-hidden" onClick={e => e.stopPropagation()}>
                            {[
                              { key: "specification_check", label: "Specification Mismatch" },
                              { key: "quantity_check", label: "Quantity Shortage" },
                              { key: "quality_check", label: "Quality Damage" },
                            ].map((opt) => {
                              const checked = normalizeCheckValue(material?.[opt.key]) === "fault";
                              return (
                                <label key={opt.key} className="flex items-center justify-between px-4 py-3 text-xs font-semibold hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0 text-slate-700">
                                  <span>{opt.label}</span>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFaultCategory(material, opt.key)}
                                    className="h-4 w-4 accent-red-500 rounded"
                                  />
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {normalizeCheckValue(material?.quantity_check) === "fault" && (
                        <div>
                          <label className="block text-[10px] font-bold text-red-600 uppercase tracking-widest mb-1.5">Short Quantity</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              placeholder="0"
                              value={material.false_quantity ?? ""}
                              onChange={(e) => updateFaultyQuantity(material.id, e.target.value)}
                              className="w-32 px-3 py-2 text-xs font-bold border border-red-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20"
                            />
                            <span className="text-xs font-bold text-red-700/60 uppercase">{material.unit}</span>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-[10px] font-bold text-red-600 uppercase tracking-widest mb-1.5">Fault Details</label>
                        <textarea
                          rows={2}
                          value={material.specification_check_comment || ""}
                          onChange={(e) => updateFaultDetails(material.id, e.target.value)}
                          placeholder="What exactly is wrong?"
                          className="w-full px-3 py-2 text-xs font-medium border border-red-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 resize-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 border-t border-slate-100 bg-white shrink-0">
        <div className="flex gap-3">
          <button
            className={`flex-1 bg-white border-2 border-slate-200 text-slate-600 hover:bg-slate-50 py-3 rounded-xl flex items-center justify-center text-xs font-bold transition-all ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleReturn}
            disabled={isSubmitting}
          >
            <RotateCcw size={16} className="mr-2" />
            RETURN ITEMS
          </button>
          <button
            className={`flex-[1.5] bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl flex items-center justify-center text-xs font-bold transition-all shadow-lg shadow-emerald-200 hover:shadow-emerald-300 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleMarkAsReceived}
            disabled={isSubmitting}
          >
            <CheckCircle2 size={16} className="mr-2" />
            {isSubmitting ? "PROCESSING..." : "MARK COMPLETED"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryFlow;
